import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Button, IconButton, Skeleton, Alert,
    ButtonGroup, Tooltip
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ReservationService } from '../api/services/reservationService';
import { RoomService } from '../api/services/roomService';

// Helper to format Date to YYYY-MM-DD
const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper to add days to a Date
const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export default function BookingCalendar({ tenantId, onMoveRoomClick, setNotification }) {
    const [viewMode, setViewMode] = useState('week'); // 'week', 'two-weeks', 'month'
    const [currentDate, setCurrentDate] = useState(new Date());
    const [bookings, setBookings] = useState([]);
    const [allRooms, setAllRooms] = useState([]);
    const [loading, setLoading] = useState(false);

    // Calculate startDate and endDate based on viewMode and currentDate
    const dateRange = useMemo(() => {
        let start = new Date(currentDate);
        start.setHours(0, 0, 0, 0);
        let end = new Date(currentDate);
        end.setHours(0, 0, 0, 0);

        if (viewMode === 'month') {
            start.setDate(1); // Start of month
            end = new Date(start.getFullYear(), start.getMonth() + 1, 0); // End of month
        } else if (viewMode === 'two-weeks') {
            const dayOfWeek = start.getDay();
            start = addDays(start, -dayOfWeek); // Start of week (Sunday)
            end = addDays(start, 13); // 14 days later
        } else {
            // week
            const dayOfWeek = start.getDay();
            start = addDays(start, -dayOfWeek); // Start of week (Sunday)
            end = addDays(start, 6); // 7 days later
        }

        return {
            startStr: formatDate(start),
            endStr: formatDate(end),
            startDate: start,
            endDate: end
        };
    }, [currentDate, viewMode]);

    // Generate array of date strings for the grid columns
    const daysColumns = useMemo(() => {
        const columns = [];
        let curr = new Date(dateRange.startDate);
        while (curr <= dateRange.endDate) {
            columns.push({
                dateObj: new Date(curr),
                dateStr: formatDate(curr),
                dayLabel: curr.toLocaleDateString('en-US', { weekday: 'short' }),
                dayNum: curr.getDate()
            });
            curr = addDays(curr, 1);
        }
        return columns;
    }, [dateRange]);

    useEffect(() => {
        const fetchCalendar = async () => {
            if (!tenantId) return;
            setLoading(true);
            try {
                const [calData, roomsData] = await Promise.all([
                    ReservationService.getCalendar(dateRange.startStr, dateRange.endStr, tenantId),
                    RoomService.getAll({ noPagination: true })
                ]);
                
                const bookingsArray = Array.isArray(calData) ? calData : (calData.data || []);
                setBookings(bookingsArray);
                
                const roomsArray = Array.isArray(roomsData) ? roomsData : (roomsData.data || roomsData.rooms || []);
                setAllRooms(roomsArray);
            } catch (err) {
                console.error("Calendar fetch error:", err);
                if (setNotification) {
                    setNotification({
                        open: true,
                        message: err?.response?.data?.message || err.message || 'Failed to fetch calendar data.',
                        severity: 'error'
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        fetchCalendar();
    }, [dateRange.startStr, dateRange.endStr, tenantId]);

    const handlePrev = () => {
        if (viewMode === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        } else if (viewMode === 'two-weeks') {
            setCurrentDate(addDays(currentDate, -14));
        } else {
            setCurrentDate(addDays(currentDate, -7));
        }
    };

    const handleNext = () => {
        if (viewMode === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        } else if (viewMode === 'two-weeks') {
            setCurrentDate(addDays(currentDate, 14));
        } else {
            setCurrentDate(addDays(currentDate, 7));
        }
    };

    // Group bookings by room
    const roomsMap = {};
    // Seed with all physical rooms first
    allRooms.forEach(r => {
        const rName = r.roomNumber || r.name || r.id;
        roomsMap[rName] = [];
    });
    
    // Overlay the active bookings onto the corresponding room lanes
    bookings.forEach(b => {
        const roomName = b.room?.roomNumber || b.room?.name || b.roomNumber || b.roomId || 'Unassigned';
        if (!roomsMap[roomName]) roomsMap[roomName] = [];
        roomsMap[roomName].push(b);
    });
    
    // Sort room names numerically (e.g. 101, 102, 500)
    const roomKeys = Object.keys(roomsMap).sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
    });

    return (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarMonthIcon color="primary" fontSize="large" />
                    <Typography variant="h5" fontWeight="bold">
                        Booking Timeline
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <ButtonGroup size="small">
                        <Button 
                            variant={viewMode === 'week' ? 'contained' : 'outlined'} 
                            onClick={() => setViewMode('week')}
                        >
                            Week
                        </Button>
                        <Button 
                            variant={viewMode === 'two-weeks' ? 'contained' : 'outlined'} 
                            onClick={() => setViewMode('two-weeks')}
                        >
                            2 Weeks
                        </Button>
                        <Button 
                            variant={viewMode === 'month' ? 'contained' : 'outlined'} 
                            onClick={() => setViewMode('month')}
                        >
                            Month
                        </Button>
                    </ButtonGroup>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={handlePrev} size="small" sx={{ border: '1px solid #ccc' }} disabled={loading}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 140, textAlign: 'center' }}>
                            {dateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {dateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        <IconButton onClick={handleNext} size="small" sx={{ border: '1px solid #ccc' }} disabled={loading}>
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* Grid Container */}
            <Paper 
                sx={{ 
                    borderRadius: 2, 
                    border: '1px solid #e0e0e0', 
                    overflowX: 'auto',
                    position: 'relative',
                    minHeight: 400
                }}
            >
                {loading && (
                    <Box 
                        sx={{ 
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                            backgroundColor: 'rgba(255,255,255,0.7)', 
                            zIndex: 10, display: 'flex', flexDirection: 'column', p: 2 
                        }}
                    >
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                    </Box>
                )}

                <Box sx={{ display: 'inline-flex', flexDirection: 'column', minWidth: '100%' }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', borderBottom: '2px solid #e0e0e0', bgcolor: '#f8fafc' }}>
                        <Box sx={{ width: 150, flexShrink: 0, p: 2, borderRight: '1px solid #e0e0e0', fontWeight: 'bold' }}>
                            Room
                        </Box>
                        {daysColumns.map((col) => (
                            <Box 
                                key={col.dateStr} 
                                sx={{ 
                                    width: 80, 
                                    flexShrink: 0, 
                                    p: 1, 
                                    borderRight: '1px solid #e0e0e0', 
                                    textAlign: 'center',
                                    bgcolor: col.dayLabel === 'Sat' || col.dayLabel === 'Sun' ? '#f1f5f9' : 'inherit'
                                }}
                            >
                                <Typography variant="caption" display="block" color="text.secondary">{col.dayLabel}</Typography>
                                <Typography variant="body2" fontWeight="bold">{col.dayNum}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Rooms Rows */}
                    {roomKeys.length === 0 && !loading && (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No bookings found for this period.</Typography>
                        </Box>
                    )}
                    {roomKeys.map(room => {
                        const getLocalDate = (isoString) => new Date(isoString.split('T')[0] + 'T00:00:00');
                        
                        // Calculate lanes to prevent overlapping bookings from stacking visually
                        const sortedBookings = [...roomsMap[room]].sort((a, b) => {
                            const aStart = getLocalDate(a.checkIn || a.startDate).getTime();
                            const bStart = getLocalDate(b.checkIn || b.startDate).getTime();
                            return aStart - bStart;
                        });
                        
                        const lanes = [];
                        const bookingWithLane = sortedBookings.map(booking => {
                            const start = getLocalDate(booking.checkIn || booking.startDate).getTime();
                            const end = getLocalDate(booking.checkOut || booking.endDate).getTime();
                            
                            let laneIndex = 0;
                            while (true) {
                                // Free if no previous booking in this lane, or previous booking checkout is <= this checkin
                                if (!lanes[laneIndex] || lanes[laneIndex] <= start) {
                                    lanes[laneIndex] = end;
                                    break;
                                }
                                laneIndex++;
                            }
                            return { booking, laneIndex };
                        });
                        
                        const maxLanes = Math.max(1, lanes.length);
                        const rowMinHeight = Math.max(50, maxLanes * 40 + 16);

                        return (
                        <Box 
                            key={room} 
                            sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', position: 'relative', minHeight: rowMinHeight }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const resId = e.dataTransfer.getData("resId");
                                if (resId && onMoveRoomClick) {
                                    const booking = bookings.find(b => b.id === resId);
                                    if (booking) {
                                        onMoveRoomClick(booking);
                                    }
                                }
                            }}
                        >
                            <Box sx={{ width: 150, flexShrink: 0, p: 2, borderRight: '1px solid #e0e0e0', fontWeight: 'medium', bgcolor: 'white' }}>
                                {room}
                            </Box>
                            {/* Grid cells */}
                            {daysColumns.map((col) => (
                                <Box 
                                    key={col.dateStr} 
                                    sx={{ 
                                        width: 80, 
                                        flexShrink: 0, 
                                        borderRight: '1px solid #e0e0e0',
                                        bgcolor: col.dayLabel === 'Sat' || col.dayLabel === 'Sun' ? '#f8fafc' : 'white'
                                    }}
                                />
                            ))}
                            {/* Booking Blocks overlay */}
                            {bookingWithLane.map(({ booking, laneIndex }) => {
                                const checkInDate = getLocalDate(booking.checkIn || booking.startDate);
                                const checkOutDate = getLocalDate(booking.checkOut || booking.endDate);
                                
                                // Intersect booking dates with visible range
                                if (checkOutDate < dateRange.startDate || checkInDate > dateRange.endDate) return null;

                                const startObj = checkInDate < dateRange.startDate ? dateRange.startDate : checkInDate;
                                const endObj = checkOutDate > dateRange.endDate ? dateRange.endDate : checkOutDate;

                                // Calculate positions
                                const dayWidth = 80;
                                const startDiff = Math.round((startObj - dateRange.startDate) / (1000 * 60 * 60 * 24));
                                const spanDays = Math.round((endObj - startObj) / (1000 * 60 * 60 * 24));
                                
                                // If 0 days (checkin and checkout same day), make it 1 day block
                                const finalSpan = spanDays > 0 ? spanDays : 1;

                                const left = 150 + (startDiff * dayWidth);
                                const width = finalSpan * dayWidth;
                                const topOffset = 8 + (laneIndex * 40);

                                return (
                                    <Tooltip key={booking.id} title={`${booking.guest?.name || booking.guestName || 'Guest'} (${formatDate(checkInDate)} - ${formatDate(checkOutDate)})`}>
                                        <Box 
                                            sx={{
                                                position: 'absolute',
                                                top: topOffset,
                                                left: left,
                                                width: width - 4, // 2px margin on sides
                                                height: 32,
                                                bgcolor: booking.status === 'In-house' ? '#10b981' : booking.status === 'Checked-out' ? '#6b7280' : (booking.status === 'Cancelled' || booking.status === 'No-show') ? '#ef4444' : '#3b82f6',
                                                color: 'white',
                                                borderRadius: 1,
                                                p: 0.5,
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                                boxShadow: 1,
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                zIndex: 5
                                            }}
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData("resId", booking.id);
                                            }}
                                        >
                                            {booking.guest?.name || booking.guestName || 'Booked'}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    )})}
                </Box>
            </Paper>
        </Box>
    );
}
